import React, { useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { reverbAffiliateUrl } from '../../utils/affiliateLinks';

const API_URL = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' && window.location.port === '3002'
    ? 'http://localhost:3000/api'
    : '/api');

// ── helpers ───────────────────────────────────────────────────────────────────
function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}
function fmtMoney(val) {
  if (val == null) return '—';
  return `$${Number(val).toLocaleString()}`;
}

// ── styles ────────────────────────────────────────────────────────────────────
const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0a0a0f',
    color: 'white',
    padding: '2rem',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  heading: { fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.25rem', color: 'white' },
  subheading: { fontSize: '0.9rem', color: 'rgba(255,255,255,0.5)', marginBottom: '2rem' },
  card: {
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '12px',
    padding: '1.5rem',
    marginBottom: '1.5rem',
  },
  searchRow: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' },
  label: { display: 'block', fontSize: '0.8rem', color: 'rgba(255,255,255,0.6)', marginBottom: '0.3rem' },
  input: {
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: 'white',
    padding: '0.5rem 0.75rem',
    fontSize: '0.9rem',
    outline: 'none',
    width: '100%',
  },
  fieldGroup: { flex: '1 1 180px' },
  btnPrimary: {
    background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
    border: 'none',
    borderRadius: '8px',
    color: 'white',
    cursor: 'pointer',
    fontWeight: '600',
    padding: '0.5rem 1.25rem',
    fontSize: '0.9rem',
    alignSelf: 'flex-end',
    whiteSpace: 'nowrap',
  },
  btnDanger: {
    background: 'rgba(239,68,68,0.15)',
    border: '1px solid rgba(239,68,68,0.4)',
    borderRadius: '8px',
    color: '#f87171',
    cursor: 'pointer',
    fontWeight: '600',
    padding: '0.4rem 0.9rem',
    fontSize: '0.85rem',
  },
  btnGhost: {
    background: 'transparent',
    border: '1px solid rgba(255,255,255,0.2)',
    borderRadius: '8px',
    color: 'rgba(255,255,255,0.7)',
    cursor: 'pointer',
    padding: '0.4rem 0.9rem',
    fontSize: '0.85rem',
  },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: {
    textAlign: 'left',
    padding: '0.6rem 0.75rem',
    fontSize: '0.75rem',
    fontWeight: '600',
    color: 'rgba(255,255,255,0.5)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  td: {
    padding: '0.75rem',
    fontSize: '0.875rem',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    verticalAlign: 'middle',
  },
  badge: (color) => ({
    display: 'inline-block',
    padding: '0.2rem 0.5rem',
    borderRadius: '6px',
    fontSize: '0.75rem',
    fontWeight: '600',
    background: color === 'green' ? 'rgba(34,197,94,0.15)' : color === 'red' ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.1)',
    color: color === 'green' ? '#4ade80' : color === 'red' ? '#f87171' : 'rgba(255,255,255,0.7)',
  }),
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  },
  modal: {
    background: '#12121a',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '16px',
    padding: '2rem',
    maxWidth: '720px',
    width: '100%',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  modalHeading: { fontSize: '1.1rem', fontWeight: '700', marginBottom: '1.25rem', color: 'white' },
  grid2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem 1.5rem', marginBottom: '1rem' },
  detailLabel: { fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.15rem' },
  detailValue: { fontSize: '0.9rem', color: 'white', wordBreak: 'break-all' },
  pre: {
    background: 'rgba(0,0,0,0.4)',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '8px',
    padding: '0.75rem',
    fontSize: '0.8rem',
    color: 'rgba(255,255,255,0.8)',
    overflowX: 'auto',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    marginBottom: '1rem',
  },
  sectionTitle: { fontSize: '0.8rem', fontWeight: '600', color: 'rgba(255,255,255,0.5)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.5rem', marginTop: '1rem' },
  error: { color: '#f87171', fontSize: '0.875rem', marginTop: '0.5rem' },
  refundForm: {
    background: 'rgba(239,68,68,0.08)',
    border: '1px solid rgba(239,68,68,0.3)',
    borderRadius: '10px',
    padding: '1rem',
    marginTop: '1rem',
  },
  textarea: {
    width: '100%',
    background: 'rgba(255,255,255,0.08)',
    border: '1px solid rgba(255,255,255,0.15)',
    borderRadius: '8px',
    color: 'white',
    padding: '0.6rem 0.75rem',
    fontSize: '0.875rem',
    resize: 'vertical',
    minHeight: '80px',
    outline: 'none',
    boxSizing: 'border-box',
  },
};

// =============================================================================
// RenderedResult — mirrors the customer-facing valuation report view.
// Renders result_snapshot + query_input exactly as the customer received it.
// =============================================================================
function confidenceColor(c) {
  if (c === 'high') return '#4ade80';
  if (c === 'medium') return '#fbbf24';
  return '#f87171';
}
function RenderedResult({ result, queryInput }) {
  if (!result) return null;
  const qi = queryInput || {};
  const ampTitle = [qi.make, qi.model].filter(Boolean).join(' ');
  const ampMeta = [qi.year, qi.condition].filter(Boolean).join(' · ');
  const currency = v => v != null ? `$${Number(v).toLocaleString()}` : '—';
  return (
    <div style={{ color: 'white', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Header */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.7rem', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.4)', marginBottom: '0.25rem' }}>AmpNebula Valuation™</div>
        {ampTitle && <div style={{ fontSize: '1.1rem', fontWeight: '700' }}>{ampTitle}</div>}
        {ampMeta && <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.15rem' }}>{ampMeta}</div>}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          {result.category && (
            <span style={{ background: 'rgba(59,130,246,0.2)', border: '1px solid rgba(59,130,246,0.4)', borderRadius: '6px', padding: '0.15rem 0.5rem', fontSize: '0.75rem', color: '#93c5fd' }}>
              {result.category.replace(/_/g, ' ')}
            </span>
          )}
          {result.confidence && (
            <span style={{ background: 'rgba(255,255,255,0.08)', borderRadius: '6px', padding: '0.15rem 0.5rem', fontSize: '0.75rem', fontWeight: '700', color: confidenceColor(result.confidence) }}>
              {result.confidence.toUpperCase()} CONFIDENCE
            </span>
          )}
        </div>
      </div>

      {/* Values */}
      <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem', marginBottom: '1rem' }}>
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.25rem' }}>Estimated Fair Market Value</div>
        <div style={{ fontSize: '1.75rem', fontWeight: '800', color: '#74b9ff', marginBottom: '0.75rem' }}>{currency(result.estimated_value)}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '0.5rem 1rem' }}>
          {[['Low', result.estimated_value_low], ['High', result.estimated_value_high], ['Trade-in', result.trade_in_value], ['Insurance / Replacement', result.insurance_value]]
            .filter(([, v]) => v != null)
            .map(([label, val]) => (
              <div key={label}>
                <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)' }}>{label}</div>
                <div style={{ fontSize: '0.95rem', fontWeight: '600' }}>{currency(val)}</div>
              </div>
            ))}
        </div>
      </div>

      {/* Analysis */}
      {result.reasoning && (
        <div style={{ marginBottom: '0.875rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)', marginBottom: '0.35rem' }}>Analysis</div>
          <p style={{ fontSize: '0.875rem', lineHeight: '1.6', color: 'rgba(255,255,255,0.8)', margin: 0 }}>{result.reasoning}</p>
        </div>
      )}

      {/* Reverb price guide */}
      {result.reverb_price_guide && (
        <div style={{ marginBottom: '0.875rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)', marginBottom: '0.35rem' }}>Reverb Price Guide</div>
          <p style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', margin: 0 }}>
            <a href={reverbAffiliateUrl(result.reverb_price_guide.webUrl)} target="_blank" rel="noopener noreferrer" style={{ color: '#74b9ff' }}>
              {result.reverb_price_guide.title}
            </a>
            {' — '}{currency(result.reverb_price_guide.estimatedLow)}{' – '}{currency(result.reverb_price_guide.estimatedHigh)}
          </p>
        </div>
      )}

      {/* Comparable sales */}
      {result.comparable_sales && (
        <div style={{ marginBottom: '0.875rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)', marginBottom: '0.35rem' }}>Comparable Sales</div>
          <ul style={{ margin: 0, paddingLeft: '1.25rem', fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)', lineHeight: '1.7' }}>
            {(Array.isArray(result.comparable_sales) ? result.comparable_sales : result.comparable_sales.split('\n')).filter(l => l.trim()).map((line, i) => <li key={i}>{line.trim()}</li>)}
          </ul>
        </div>
      )}

      {/* Market factors */}
      {result.market_factors && (
        <div style={{ marginBottom: '0.5rem' }}>
          <div style={{ fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em', color: 'rgba(255,255,255,0.4)', marginBottom: '0.35rem' }}>Market Context</div>
          <p style={{ fontSize: '0.875rem', lineHeight: '1.6', color: 'rgba(255,255,255,0.8)', margin: 0 }}>{result.market_factors}</p>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// ValuationSupportScreen
// =============================================================================
export default function ValuationSupportScreen() {
  const { token } = useAuth();

  // Search state
  const [email, setEmail] = useState('');
  const [refCode, setRefCode] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // Results state
  const [records, setRecords] = useState(null);
  const [total, setTotal] = useState(0);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');

  // Detail modal state
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState('');

  // Result snapshot tab: 'rendered' | 'json'
  const [resultTab, setResultTab] = useState('rendered');

  // Refund form state
  const [showRefundForm, setShowRefundForm] = useState(false);
  const [refundReason, setRefundReason] = useState('');
  const [refunding, setRefunding] = useState(false);
  const [refundError, setRefundError] = useState('');

  // ── search ──────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async (e) => {
    if (e) e.preventDefault();
    setSearching(true);
    setSearchError('');
    setRecords(null);

    const params = new URLSearchParams({ limit: 100 });
    if (email.trim()) params.set('email', email.trim());
    if (refCode.trim()) params.set('reference_code', refCode.trim().toUpperCase());
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);

    try {
      const res = await fetch(`${API_URL}/valuations?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Search failed');
      setRecords(data.records);
      setTotal(data.total);
    } catch (err) {
      console.error('[ValuationSupport] search error:', err);
      setSearchError(err.message);
    } finally {
      setSearching(false);
    }
  }, [token, email, refCode, dateFrom, dateTo]);

  // ── load detail ─────────────────────────────────────────────────────────────
  const handleSelectRecord = useCallback(async (valuation_id) => {
    setLoadingDetail(true);
    setDetailError('');
    setShowRefundForm(false);
    setRefundReason('');
    setRefundError('');
    setResultTab('rendered');
    setSelected(null);

    try {
      const res = await fetch(`${API_URL}/valuations/${valuation_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load record');
      setSelected(data);
    } catch (err) {
      console.error('[ValuationSupport] load detail error:', err);
      setDetailError(err.message);
      setSelected({});
    } finally {
      setLoadingDetail(false);
    }
  }, [token]);

  // ── mark refunded ────────────────────────────────────────────────────────────
  const handleRefund = useCallback(async () => {
    if (!refundReason.trim()) { setRefundError('Please enter a refund reason.'); return; }
    setRefunding(true);
    setRefundError('');

    try {
      const res = await fetch(`${API_URL}/valuations/${selected.valuation_id}/refund`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ refund_reason: refundReason.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to mark refunded');
      // Update state
      setSelected(prev => ({ ...prev, refunded: true, refunded_at: data.refunded_at, refund_reason: data.refund_reason }));
      setRecords(prev => prev?.map(r =>
        r.valuation_id === selected.valuation_id ? { ...r, refunded: true } : r
      ));
      setShowRefundForm(false);
    } catch (err) {
      console.error('[ValuationSupport] refund error:', err);
      setRefundError(err.message);
    } finally {
      setRefunding(false);
    }
  }, [token, selected, refundReason]);

  // ── render ───────────────────────────────────────────────────────────────────
  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Valuation Support</h1>
      <p style={styles.subheading}>Search valuation records by reference code, customer email, or date range.</p>

      {/* Search form */}
      <div style={styles.card}>
        <form onSubmit={handleSearch}>
          <div style={styles.searchRow}>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Reference Code</label>
              <input style={styles.input} placeholder="VAL-20260323-ABC123" value={refCode}
                onChange={e => setRefCode(e.target.value)} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>Customer Email</label>
              <input style={styles.input} placeholder="partial or full email" value={email}
                onChange={e => setEmail(e.target.value)} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>From Date</label>
              <input style={styles.input} type="date" value={dateFrom}
                onChange={e => setDateFrom(e.target.value)} />
            </div>
            <div style={styles.fieldGroup}>
              <label style={styles.label}>To Date</label>
              <input style={styles.input} type="date" value={dateTo}
                onChange={e => setDateTo(e.target.value)} />
            </div>
            <button type="submit" style={styles.btnPrimary} disabled={searching}>
              {searching ? 'Searching…' : 'Search'}
            </button>
          </div>
          {searchError && <p style={styles.error}>{searchError}</p>}
        </form>
      </div>

      {/* Results table */}
      {records !== null && (
        <div style={styles.card}>
          <div style={{ marginBottom: '0.75rem', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)' }}>
            {total} record{total !== 1 ? 's' : ''} found
          </div>
          {records.length === 0 ? (
            <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '2rem 0' }}>
              No records match your search.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Reference</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Email</th>
                    <th style={styles.th}>Amp Queried</th>
                    <th style={styles.th}>Value Range</th>
                    <th style={styles.th}>Source</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {records.map(r => {
                    const qi = r.query_input || {};
                    const ampLabel = [qi.make, qi.model, qi.year].filter(Boolean).join(' ');
                    return (
                      <tr key={r.valuation_id}>
                        <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.reference_code}</td>
                        <td style={styles.td}>{fmt(r.created_at)}</td>
                        <td style={styles.td}>{r.email}</td>
                        <td style={styles.td}>{ampLabel || '—'}</td>
                        <td style={styles.td}>
                          {r.estimated_value_low && r.estimated_value_high
                            ? `${fmtMoney(r.estimated_value_low)} – ${fmtMoney(r.estimated_value_high)}`
                            : fmtMoney(r.estimated_value)}
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(null)}>
                            {r.source === 'collection' ? 'Collection' : 'Public'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={styles.badge(r.refunded ? 'red' : r.source === 'collection' ? null : 'green')}>
                            {r.refunded ? 'Refunded' : r.source === 'collection' ? 'Subscription' : 'Paid'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <button style={styles.btnGhost} onClick={() => handleSelectRecord(r.valuation_id)}>
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Detail modal */}
      {(loadingDetail || selected !== null) && (
        <div style={styles.overlay} onClick={() => setSelected(null)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            {loadingDetail ? (
              <p style={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading…</p>
            ) : detailError ? (
              <p style={styles.error}>{detailError}</p>
            ) : (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.25rem' }}>
                  <div>
                    <div style={{ fontFamily: 'monospace', fontSize: '1.1rem', fontWeight: '700', color: '#74b9ff' }}>
                      {selected.reference_code}
                    </div>
                    <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.2rem' }}>
                      {fmt(selected.created_at)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                    {selected.source !== 'collection' && selected.stripe_dashboard_url && (
                      <a
                        href={selected.stripe_dashboard_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ ...styles.btnGhost, textDecoration: 'none', display: 'inline-block' }}
                      >
                        View in Stripe ↗
                      </a>
                    )}
                    <button style={styles.btnGhost} onClick={() => setSelected(null)}>✕ Close</button>
                  </div>
                </div>

                <div style={styles.grid2}>
                  <div>
                    <div style={styles.detailLabel}>Customer Email</div>
                    <div style={styles.detailValue}>{selected.email}</div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Account</div>
                    <div style={styles.detailValue}>{selected.username || 'Anonymous'}</div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Source</div>
                    <div style={styles.detailValue}>
                      <span style={styles.badge(null)}>
                        {selected.source === 'collection' ? 'Collection (Subscription)' : 'Public (Paid)'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Status</div>
                    <div style={styles.detailValue}>
                      <span style={styles.badge(selected.refunded ? 'red' : selected.source === 'collection' ? null : 'green')}>
                        {selected.refunded ? 'Refunded' : selected.source === 'collection' ? 'Active Subscription' : 'Paid'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Cert Downloaded</div>
                    <div style={styles.detailValue}>{selected.certificate_generated ? 'Yes' : 'No'}</div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Confirmation Viewed</div>
                    <div style={styles.detailValue}>{selected.confirmation_viewed_at ? fmt(selected.confirmation_viewed_at) : 'No'}</div>
                  </div>
                  <div>
                    <div style={styles.detailLabel}>Conf. Email Sent</div>
                    <div style={styles.detailValue}>{selected.confirmation_email_sent ? 'Yes' : 'No'}</div>
                  </div>
                </div>

                {selected.refunded && (
                  <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '8px', padding: '0.75rem', marginBottom: '1rem' }}>
                    <div style={styles.detailLabel}>Refunded {fmt(selected.refunded_at)}</div>
                    <div style={{ fontSize: '0.875rem', color: '#fca5a5', marginTop: '0.25rem' }}>{selected.refund_reason}</div>
                  </div>
                )}

                <div style={styles.sectionTitle}>Query Input (what customer submitted)</div>
                <pre style={styles.pre}>{JSON.stringify(selected.query_input, null, 2)}</pre>

                {/* Result snapshot — tabbed: rendered report vs raw JSON */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', marginBottom: '0.5rem' }}>
                  <div style={{ ...styles.sectionTitle, margin: 0 }}>Result Delivered to Customer</div>
                  <div style={{ display: 'flex', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '2px', gap: '2px' }}>
                    {['rendered', 'json'].map(tab => (
                      <button
                        key={tab}
                        onClick={() => setResultTab(tab)}
                        style={{
                          background: resultTab === tab ? 'rgba(255,255,255,0.12)' : 'transparent',
                          border: 'none',
                          borderRadius: '6px',
                          color: resultTab === tab ? 'white' : 'rgba(255,255,255,0.45)',
                          cursor: 'pointer',
                          fontSize: '0.78rem',
                          fontWeight: '600',
                          padding: '0.25rem 0.65rem',
                        }}
                      >
                        {tab === 'rendered' ? 'Report View' : 'Raw JSON'}
                      </button>
                    ))}
                  </div>
                </div>
                {resultTab === 'rendered'
                  ? (
                    <div style={{ background: 'rgba(0,0,0,0.35)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1.25rem', marginBottom: '1rem' }}>
                      <RenderedResult result={selected.result_snapshot} queryInput={selected.query_input} />
                    </div>
                  ) : (
                    <pre style={styles.pre}>{JSON.stringify(selected.result_snapshot, null, 2)}</pre>
                  )
                }

                {/* Refund form — only applicable to public paid valuations */}
                {selected.source !== 'collection' && !selected.refunded && (
                  <>
                    {!showRefundForm ? (
                      <div style={{ marginTop: '1rem', textAlign: 'right' }}>
                        <button style={styles.btnDanger} onClick={() => setShowRefundForm(true)}>
                          Mark as Refunded
                        </button>
                      </div>
                    ) : (
                      <div style={styles.refundForm}>
                        <div style={{ fontSize: '0.875rem', color: '#fca5a5', marginBottom: '0.75rem', fontWeight: '600' }}>
                          ⚠ Issue the refund in the Stripe dashboard first, then mark it here.
                        </div>
                        <label style={styles.label}>Refund Reason (required)</label>
                        <textarea
                          style={styles.textarea}
                          placeholder="e.g. Customer requested refund — confidence mismatch"
                          value={refundReason}
                          onChange={e => setRefundReason(e.target.value)}
                        />
                        {refundError && <p style={styles.error}>{refundError}</p>}
                        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem', justifyContent: 'flex-end' }}>
                          <button style={styles.btnGhost} onClick={() => { setShowRefundForm(false); setRefundReason(''); setRefundError(''); }}>
                            Cancel
                          </button>
                          <button style={styles.btnDanger} onClick={handleRefund} disabled={refunding}>
                            {refunding ? 'Saving…' : 'Confirm Mark Refunded'}
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
